import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { api, type Query } from "../api/client.ts";

export const PAGE_SIZES = [25, 50, 100] as const;

export type Paged<T> = { items: T[]; has_more: boolean; total?: number };

/** Page + rows-per-page in the URL (`page`, `limit`, optionally prefixed when a page has several tables). */
export function usePageParams(prefix = "", defaultLimit = 50) {
  const [params, setParams] = useSearchParams();
  const pk = `${prefix}page`;
  const lk = `${prefix}limit`;
  const page = Math.max(1, Number(params.get(pk)) || 1);
  const rawLimit = Number(params.get(lk));
  const limit = (PAGE_SIZES as readonly number[]).includes(rawLimit) ? rawLimit : defaultLimit;
  const set = useCallback(
    (next: { page?: number; limit?: number }) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          if (next.page !== undefined) {
            if (next.page <= 1) n.delete(pk);
            else n.set(pk, String(next.page));
          }
          if (next.limit !== undefined) {
            if (next.limit === defaultLimit) n.delete(lk);
            else n.set(lk, String(next.limit));
          }
          return n;
        },
        { replace: true },
      ),
    [setParams, pk, lk, defaultLimit],
  );
  return { page, limit, setPage: (p: number) => set({ page: p }), setLimit: (l: number) => set({ limit: l, page: 1 }) };
}

/**
 * Numbered pages from the server (`?page=&limit=` → `{ items, total }`). Filters go in `params`;
 * when they change the list goes back to page 1.
 */
export function usePagedList<T extends { id: string }, R extends Paged<T> = Paged<T>>(path: string, params: Query, prefix = "", defaultLimit = 50) {
  const { page, limit, setPage, setLimit } = usePageParams(prefix, defaultLimit);
  const [data, setData] = useState<R | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const key = JSON.stringify(params);
  const lastKey = useRef(key);
  const seq = useRef(0);

  // New filters → page 1 (keeps the rest of the URL).
  useEffect(() => {
    if (lastKey.current !== key) {
      lastKey.current = key;
      if (page !== 1) setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const load = useCallback(async () => {
    const id = ++seq.current;
    if (!path) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<R>(path, { ...params, page, limit });
      if (id === seq.current) setData(r);
    } catch (e) {
      if (id === seq.current) setError(e);
    } finally {
      if (id === seq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, page, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  const replace = useCallback((item: T) => setData((d) => (d ? { ...d, items: d.items.map((x) => (x.id === item.id ? item : x)) } : d)), []);
  return {
    data, items: data?.items ?? [], total: data?.total, hasMore: data?.has_more ?? false,
    page, limit, setPage, setLimit, loading, error, reload: load, replace,
  };
}

/** The same pager for small lists the API returns in one go. */
export function useClientPages<T>(items: T[], prefix: string, defaultLimit = 25) {
  const { page, limit, setPage, setLimit } = usePageParams(prefix, defaultLimit);
  const pages = Math.max(1, Math.ceil(items.length / limit));
  const p = Math.min(page, pages);
  return { rows: items.slice((p - 1) * limit, p * limit), page: p, limit, total: items.length, setPage, setLimit };
}
